<script setup>
import { ref, computed, watch, nextTick, defineProps, defineEmits } from "vue";

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => null,
  },
  disabledDate: {
    type: Function,
    default: null,
  },
  startPlaceholder: {
    type: String,
    default: "",
  },
  endPlaceholder: {
    type: String,
    default: "",
  },
  triggerHeight: {
    type: String,
    default: "32px",
  },
});

const emit = defineEmits(["update:modelValue"]);

const showTimePicker = ref(false);
const selectedDate = ref(null);
const startTime = ref("");
const endTime = ref("");
const hiddenDatePickerRef = ref(null);

watch(
  () => props.modelValue,
  (val) => {
    if (val && val.length === 2) {
      const [datePart, timePart1] = val[0].split(" ");
      const [, timePart2] = val[1].split(" ");
      selectedDate.value = datePart;
      startTime.value = timePart1 || "";
      endTime.value = timePart2 || "";
    }
  },
  { immediate: true }
);

const displayText = computed(() => {
  if (!selectedDate.value || !startTime.value || !endTime.value) return "";
  const dateStr = selectedDate.value.split("-").reverse().join("/");
  return `${dateStr}, ${startTime.value} - ${endTime.value}`;
});

const selectedDateDisplay = computed(() => {
  if (!selectedDate.value) return "";
  return selectedDate.value.split("-").reverse().join("/");
});

const openDatePicker = () => {
  if (selectedDate.value) {
    showTimePicker.value = !showTimePicker.value;
  } else {
    showTimePicker.value = false;
    nextTick(() => hiddenDatePickerRef.value?.focus());
  }
};

const onDateChange = (val) => {
  if (!val) return;
  selectedDate.value = val;
  setTimeout(() => {
    showTimePicker.value = true;
  }, 200);
};

const onTimeConfirm = () => {
  if (!startTime.value || !endTime.value) return;
  emit("update:modelValue", [
    `${selectedDate.value} ${startTime.value}`,
    `${selectedDate.value} ${endTime.value}`,
  ]);
  showTimePicker.value = false;
};

const onTimeBack = () => {
  showTimePicker.value = false;
  nextTick(() => hiddenDatePickerRef.value?.focus());
};

const onCancel = () => {
  showTimePicker.value = false;
  if (props.modelValue && props.modelValue.length === 2) {
    const [datePart, timePart1] = props.modelValue[0].split(" ");
    const [, timePart2] = props.modelValue[1].split(" ");
    selectedDate.value = datePart;
    startTime.value = timePart1 || "";
    endTime.value = timePart2 || "";
  } else {
    selectedDate.value = null;
    startTime.value = "";
    endTime.value = "";
  }
};

const timeOptions = computed(() => {
  const opts = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 5) {
      opts.push(
        `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
      );
    }
  }
  return opts;
});

const endTimeOptions = computed(() => {
  // chọn thời gian két thúc phải lớn hơn thời gian bắt đầu
  if (!startTime.value) return timeOptions.value;
  return timeOptions.value.filter((t) => t > startTime.value);
});

watch(startTime, (val) => {
  if (endTime.value && val >= endTime.value) {
    endTime.value = "";
  }
});
</script>

<template>
  <div class="step-datetime-picker">
    <div
      class="step-datetime-picker__trigger"
      :style="{ height: triggerHeight }"
      @click="openDatePicker"
    >
      <i class="ri-time-line"></i>
      <span v-if="displayText">{{ displayText }}</span>
      <span v-else class="step-datetime-picker__placeholder">
        {{ startPlaceholder || $t("t-start-time") }} -
        {{ endPlaceholder || $t("t-end-time") }}
      </span>
    </div>

    <div class="step-datetime-picker__hidden">
      <el-date-picker
        ref="hiddenDatePickerRef"
        v-model="selectedDate"
        type="date"
        format="DD/MM/YYYY"
        value-format="YYYY-MM-DD"
        :disabled-date="disabledDate"
        popper-class="step-datetime-picker__date-popper"
        @change="onDateChange"
      />
    </div>

    <!-- chọn giờ (hiện sau khi chọn ngày) -->
    <div v-if="showTimePicker" class="step-datetime-picker__dropdown">
      <div class="step-datetime-picker__header">
        <span>{{ $t("t-step-select-time") }}</span>
        <span class="step-datetime-picker__date-badge">
          <i class="ri-calendar-line"></i>
          {{ selectedDateDisplay }}
        </span>
      </div>

      <div class="step-datetime-picker__row">
        <div class="step-datetime-picker__field">
          <label>{{ $t("t-from-time") }}</label>
          <el-select
            v-model="startTime"
            filterable
            placeholder="HH:mm"
            style="width: 100%"
            popper-class="step-datetime-picker__select-popper"
          >
            <el-option
              v-for="opt in timeOptions"
              :key="'v-' + opt"
              :label="opt"
              :value="opt"
            />
          </el-select>
        </div>
        <span class="step-datetime-picker__sep">-</span>
        <div class="step-datetime-picker__field">
          <label>{{ $t("t-to-time") }}</label>
          <el-select
            v-model="endTime"
            filterable
            placeholder="HH:mm"
            style="width: 100%"
            :disabled="!startTime"
            popper-class="step-datetime-picker__select-popper"
          >
            <el-option
              v-for="opt in endTimeOptions"
              :key="'e-' + opt"
              :label="opt"
              :value="opt"
            />
          </el-select>
        </div>
      </div>

      <div class="step-datetime-picker__actions">
        <el-button size="small" @click="onTimeBack">
          <i class="ri-arrow-left-line"></i> {{ $t("t-back") }}
        </el-button>
        <div class="step-datetime-picker__action-group">
          <el-button size="small" @click="onCancel">
            {{ $t("t-cancel") || "Hủy" }}
          </el-button>
          <el-button
            size="small"
            type="primary"
            :disabled="!startTime || !endTime"
            @click="onTimeConfirm"
          >
            <i class="ri-check-line"></i> {{ $t("t-confirm") }}
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.step-datetime-picker {
  position: relative;
  display: inline-block;
  width: 100%;

  &__trigger {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    padding: 0 12px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    background: #fff;
    cursor: pointer;
    font-size: 13px;
    color: #303133;
    transition: border-color 0.2s;
    white-space: nowrap;

    &:hover {
      border-color: #409eff;
    }

    i {
      font-size: 14px;
      color: #909399;
    }
  }

  &__placeholder {
    color: #a8abb2;
  }

  &__hidden {
    position: absolute;
    left: 50%;
    top: 0;
    width: 0;
    height: 100%;
    overflow: visible;
    opacity: 0;
    pointer-events: none;

    :deep(.el-date-editor) {
      width: 1px !important;
      min-width: 1px !important;
      padding: 0;
      border: none;
    }

    :deep(.el-input__wrapper) {
      padding: 0 !important;
      box-shadow: none !important;
    }
  }

  &__dropdown {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    z-index: 2050;
    min-width: 300px;
    padding: 16px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    border: 1px solid #e4e7ed;
    overflow: visible;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    font-size: 14px;
    font-weight: 500;
    color: #303133;
  }

  &__date-badge {
    font-size: 12px;
    color: #909399;
    display: flex;
    align-items: center;
    gap: 4px;

    i {
      font-size: 13px;
    }
  }

  &__row {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    margin-bottom: 16px;
  }

  &__field {
    flex: 1;

    label {
      display: block;
      font-size: 12px;
      color: #606266;
      margin-bottom: 4px;
    }
  }

  &__sep {
    display: flex;
    align-items: center;
    padding-bottom: 6px;
    color: #909399;
    font-size: 16px;
  }

  &__actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 8px;
    border-top: 1px solid #ebeef5;
  }

  &__action-group {
    display: flex;
    gap: 8px;
  }
}
</style>

<style lang="scss">
.step-datetime-picker__select-popper {
  z-index: 2060 !important;
}

.step-datetime-picker__date-popper {
  .el-popper__arrow {
    left: 50% !important;
    transform: translateX(-50%) !important;
  }
}
</style>
